import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const VerifyEmail = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [step, setStep] = useState('initial'); // 'initial' | 'code'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Already verified
  if (user?.email_verified) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg border-2 border-green-200">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-green-700 mb-2">Correo Verificado</h2>
          <p className="text-gray-600 mb-2">{user.email}</p>
          <p className="text-sm text-gray-500 mb-6">Tu correo electrónico ya está verificado</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 transition"
            data-testid="back-to-dashboard-btn"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const sendCode = async () => {
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/send-my-verification-code');
      setStep('code');
      setCountdown(60);
      setVerificationCode(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el código');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1);
    setVerificationCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setVerificationCode(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  const verifyCode = async () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Ingresa el código completo');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/verify-my-email', { email: user.email, code });
      setSuccess('Correo verificado correctamente');
      // Refresh user data
      if (refreshUser) await refreshUser();
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al verificar');
    } finally {
      setLoading(false);
    }
  };

  const targetEmail = user?.email;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-lg border-2 border-blue-200">
        
        {success ? (
          <div className="text-center">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-green-700 mb-2">{success}</h2>
            <p className="text-sm text-gray-500">Redirigiendo al Dashboard...</p>
          </div>
        ) : step === 'initial' ? (
          <>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                Verificar Correo
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                Enviaremos un código de verificación a {user?.email}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm">
                {error}
              </div>
            )}

            <button
              onClick={sendCode}
              disabled={loading}
              className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition disabled:opacity-50 mb-3"
              data-testid="send-verify-code-button"
            >
              {loading ? 'Enviando...' : 'Enviar Código de Verificación'}
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full text-gray-400 text-sm py-2 mt-2"
            >
              Volver al Dashboard
            </button>
          </>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">📧</div>
              <h2 className="text-xl font-bold text-gray-900">Ingresa el código</h2>
              <p className="text-gray-600 text-sm mt-1">
                Enviamos un código de 6 dígitos a:
              </p>
              <p className="font-bold text-blue-700 mt-1">{targetEmail}</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-center gap-2 mb-6" onPaste={handleCodePaste}>
              {verificationCode.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="tel"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(i, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
                  data-testid={`verify-code-input-${i}`}
                />
              ))}
            </div>

            <button
              onClick={verifyCode}
              disabled={loading || verificationCode.join('').length !== 6}
              className="w-full bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 transition disabled:opacity-50 mb-4"
              data-testid="confirm-verify-button"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>

            <div className="text-center">
              {countdown > 0 ? (
                <p className="text-gray-500 text-sm">Reenviar en <strong>{countdown}s</strong></p>
              ) : (
                <button onClick={sendCode} disabled={loading} className="text-blue-600 font-bold text-sm">
                  Reenviar código
                </button>
              )}
            </div>

            <button
              onClick={() => { setStep('initial'); setError(''); setVerificationCode(['','','','','','']); }}
              className="w-full text-gray-400 text-sm py-2 mt-4"
            >
              ← Volver
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
